<script setup>
/**
 * Et build som et kort der kan staa paa en hvilken som helst side.
 *
 * Bygget til spilsiderne, hvor to maskiner skal staa side om side midt i en
 * tekst, men holdt fri af dem: kortet ved ingenting om spil. Det kender et
 * build, en etiket og en begrundelse, og det er alt.
 *
 * Delelisten er foldet sammen fra start. Den der bare vil vide "hvad koster
 * den, og hvilket grafikkort" skal kunne se det uden at rulle, og den der vil
 * granske hver eneste del skal kunne folde ud uden at forlade siden.
 */
import { ref, computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    kort: { type: Object, required: true },
    // "Minimumskrav", "Anbefalet", "240 Hz" — hvad kortet er svar paa.
    etiket: { type: String, default: '' },
    // Hvorfor NETOP den her maskine til NETOP det her. Staar over delene.
    begrundelse: { type: String, default: '' },
    fremhaevet: { type: Boolean, default: false },
    // Delelisten aaben fra start. Slaaet fra paa sider med flere kort.
    aaben: { type: Boolean, default: false },
});

const foldetUd = ref(props.aaben);

const ikoner = {
    cpu: 'bi-cpu', koeler: 'bi-fan', mboard: 'bi-motherboard', ram: 'bi-memory',
    gfx: 'bi-gpu-card', ssd: 'bi-device-ssd', hdd: 'bi-hdd', psu: 'bi-plug',
    chassis: 'bi-pc-display', blaeser: 'bi-fan', lydkort: 'bi-music-note-beamed',
    headset: 'bi-headset', mikrofon: 'bi-mic', webcam: 'bi-camera-video', hoejttaler: 'bi-speaker', musemaatte: 'bi-square', gamerstol: 'bi-person-workspace', gamerbord: 'bi-table', ups: 'bi-battery-charging',
};

const kr = (n) => (n || n === 0 ? new Intl.NumberFormat('da-DK').format(Math.round(n)) + ' kr.' : '—');

const byggerUrl = computed(() => `/pc-bygger/ny?afsaet=${props.kort.slug}`);
const buildUrl = computed(() => `/pc-bygger/b/${props.kort.url_slug || props.kort.slug}`);

const billeder = computed(() =>
    props.kort.dele.filter((d) => d.billede_id).slice(0, 3)
);
</script>

<template>
    <article class="bk" :class="{ 'bk--frem': fremhaevet }">
        <header class="bk-top">
            <span v-if="etiket" class="bk-etiket">{{ etiket }}</span>

            <div class="bk-billeder" aria-hidden="true">
                <span v-for="d in billeder" :key="d.del_type + d.navn" class="bk-mini">
                    <picture>
                        <source :srcset="`/images/${d.billede_id}?w=96&fmt=webp`" type="image/webp" />
                        <img :src="`/images/${d.billede_id}?w=96`" :alt="''" loading="lazy"
                            @error="(e) => (e.target.style.visibility = 'hidden')" />
                    </picture>
                </span>
                <span v-if="!billeder.length" class="bk-mini"><i class="bi bi-pc-display text-muted"></i></span>
            </div>

            <h3 class="bk-navn">
                <Link :href="buildUrl">{{ kort.navn }}</Link>
            </h3>
        </header>

        <p v-if="begrundelse" class="bk-begrundelse">{{ begrundelse }}</p>

        <dl class="bk-noegle">
            <div v-for="n in kort.noegledele" :key="n.del_type">
                <dt><i class="bi" :class="ikoner[n.del_type] || 'bi-box'"></i> {{ n.type_navn }}</dt>
                <dd>{{ n.navn }}</dd>
            </div>
        </dl>

        <div class="bk-pris">
            <span class="bk-belob">{{ kr(kort.pris) }}</span>
            <!--
                Prisen er summen af delene i dag, ikke det tal der blev gemt
                da buildet blev lavet. Mangler en del sin pris, skal det staa
                der — ellers ser en ufuldstaendig sum ud som et godt tilbud.
            -->
            <span class="bk-prisnote">
                <template v-if="kort.pris_er_komplet">dagens pris, {{ kort.antal_dele }} dele</template>
                <template v-else>{{ kort.antal_dele }} dele — én eller flere mangler pris</template>
                <span v-if="kort.estimeret_watt"> &middot; ~{{ kort.estimeret_watt }} W</span>
            </span>
        </div>

        <button type="button" class="bk-fold" :aria-expanded="foldetUd" @click="foldetUd = !foldetUd">
            <i class="bi" :class="foldetUd ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            {{ foldetUd ? 'Skjul delene' : 'Se alle ' + kort.antal_dele + ' dele' }}
        </button>

        <ul v-show="foldetUd" class="bk-dele">
            <li v-for="d in kort.dele" :key="d.del_type + d.navn + d.antal">
                <i class="bi bk-ikon" :class="ikoner[d.del_type] || 'bi-box'"></i>
                <span class="bk-deltype">{{ d.type_navn }}</span>
                <a v-if="d.slug" :href="`/produkt/${d.slug}`" class="bk-delnavn">
                    <span v-if="d.antal > 1">{{ d.antal }} × </span>{{ d.navn }}
                </a>
                <span v-else class="bk-delnavn"><span v-if="d.antal > 1">{{ d.antal }} × </span>{{ d.navn }}</span>
                <span class="bk-delpris">{{ d.pris ? kr(d.pris * d.antal) : '—' }}</span>
            </li>
        </ul>

        <div class="bk-knapper">
            <Link :href="buildUrl" class="btn btn-sm btn-pricecompare-primary">
                Se hele buildet
            </Link>
            <Link :href="byggerUrl" class="btn btn-sm btn-pricecompare-ghost">
                <i class="bi bi-sliders"></i> Tilpas den
            </Link>
        </div>
    </article>
</template>

<style scoped>
.bk {
    display: flex;
    flex-direction: column;
    gap: .7rem;
    background: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg);
    padding: 1.1rem;
    height: 100%;
}
.bk--frem { border-color: var(--pc-accent); }

.bk-top { display: flex; flex-direction: column; gap: .5rem; }
.bk-etiket {
    align-self: flex-start;
    font-size: .72rem;
    text-transform: uppercase;
    letter-spacing: .06em;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: var(--pc-radius-full);
    background: var(--pc-accent-solid);
    color: var(--pc-on-accent);
}
.bk-billeder { display: flex; gap: .4rem; }
.bk-mini {
    width: 46px; height: 46px;
    display: flex; align-items: center; justify-content: center;
    background: var(--pc-bg-tertiary);
    border-radius: var(--pc-radius);
    overflow: hidden;
}
.bk-mini img { max-width: 100%; max-height: 100%; object-fit: contain; }

.bk-navn { font-size: 1.12rem; font-weight: 700; margin: 0; line-height: 1.25; }
.bk-navn a { color: var(--pc-text-primary); text-decoration: none; }
.bk-navn a:hover { color: var(--pc-accent); }

.bk-begrundelse { margin: 0; font-size: .9rem; color: var(--pc-text-secondary); }

.bk-noegle { margin: 0; display: grid; gap: .35rem; }
.bk-noegle > div { display: grid; grid-template-columns: 7.5rem 1fr; gap: .5rem; align-items: baseline; }
.bk-noegle dt { font-size: .78rem; color: var(--pc-text-muted); font-weight: 500; }
.bk-noegle dd { margin: 0; font-size: .88rem; font-weight: 600; }

.bk-pris { margin-top: auto; padding-top: .3rem; }
.bk-belob { display: block; font-size: 1.45rem; font-weight: 700; color: var(--pc-price); font-variant-numeric: tabular-nums; }
.bk-prisnote { font-size: .76rem; color: var(--pc-text-muted); }

.bk-fold {
    background: none; border: none; padding: 0;
    color: var(--pc-text-secondary); font-size: .82rem; text-align: left; cursor: pointer;
}
.bk-fold:hover { color: var(--pc-text-primary); }

.bk-dele { list-style: none; margin: 0; padding: 0; display: grid; gap: .3rem; }
.bk-dele li {
    display: grid;
    grid-template-columns: 1.1rem 6.5rem 1fr auto;
    gap: .45rem;
    align-items: baseline;
    font-size: .82rem;
    padding-bottom: .3rem;
    border-bottom: 1px solid var(--pc-border);
}
.bk-ikon { color: var(--pc-text-muted); }
.bk-deltype { color: var(--pc-text-muted); }
.bk-delnavn { color: var(--pc-text-primary); text-decoration: none; }
a.bk-delnavn:hover { color: var(--pc-accent); text-decoration: underline; }
.bk-delpris { font-variant-numeric: tabular-nums; color: var(--pc-text-secondary); white-space: nowrap; }

.bk-knapper { display: flex; gap: .5rem; flex-wrap: wrap; }

@media (max-width: 480px) {
    .bk-noegle > div { grid-template-columns: 1fr; gap: 0; }
    .bk-dele li { grid-template-columns: 1.1rem 1fr auto; }
    .bk-deltype { display: none; }
}
</style>
